import { useCallback, useRef, useState, type ChangeEvent } from "react"
import toast from "react-hot-toast"
import type { PendingEditorImage } from "../types"
import { uploadEditorImage } from "../utils/uploadEditorImage"
import { loadImageDimensions } from "../utils/loadImageDimensions"

export const useImageUpload = () => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [pendingImage, setPendingImage] = useState<PendingEditorImage | null>(
    null
  )

  const queueImageFile = useCallback(async (file: File) => {
    setIsUploading(true)
    try {
      const url = await uploadEditorImage(file)
      const alt = file.name.replace(/\.[^.]+$/, "")
      const { naturalWidth, naturalHeight } = await loadImageDimensions(url)

      setPendingImage({
        src: url,
        alt,
        naturalWidth,
        naturalHeight,
      })
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Image upload failed"
      )
    } finally {
      setIsUploading(false)
    }
  }, [])

  const clearPendingImage = useCallback(() => {
    setPendingImage(null)
  }, [])

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        void queueImageFile(file)
      }
      event.target.value = ""
    },
    [queueImageFile]
  )

  return {
    fileInputRef,
    isUploading,
    pendingImage,
    openFilePicker,
    handleFileChange,
    queueImageFile,
    clearPendingImage,
  }
}
